import type { Granularity } from '../ImplementationReport/api';
import { monthLabel } from '../ImplementationReport/period';
import { csvText } from '../ImplementationReport/rows';
import type { LibraryAssignment, LibraryCaseload, LibraryFile, LibraryReport, LibrarySchool } from './api';
import { LIBRARY } from './tokens';

export const TEACHERS_OWN = "Teacher's own";

export type Domain = 'Speaking' | 'Writing';

export const DOMAINS: Domain[] = ['Speaking', 'Writing'];

const FIXED_TYPES = ['Beacon', 'Sparks', TEACHERS_OWN];

export type Filters = { type: string; schoolsOff: Set<string>; domainsOff: Set<Domain>; teachers: Set<string> };

export type Chip = { key: string; label: string; count: number };

export function emptyFilters(): Filters {
  return { type: 'all', schoolsOff: new Set(), domainsOff: new Set(), teachers: new Set() };
}

export function typeLabel(a: LibraryAssignment): string {
  return a.templateType.trim() || TEACHERS_OWN;
}

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };

function decodeEntities(text: string): string {
  return text.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (match, code: string) => {
    if (code[0] === '#') {
      const point = code[1]?.toLowerCase() === 'x' ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
      const scalar = Number.isInteger(point) && point >= 0 && point <= 0x10ffff && (point < 0xd800 || point > 0xdfff);
      return scalar ? String.fromCodePoint(point) : match;
    }
    return ENTITIES[code.toLowerCase()] ?? match;
  });
}

export function promptText(html: string): string {
  const lines = html
    .replace(/\\(.)/g, '$1')
    .replace(/\r/g, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|blockquote|pre|tr)>/gi, '\n')
    .replace(/<\/?(p|br|div|span|em|strong|b|i|u|s|sub|sup|ul|ol|li|a|h[1-6]|blockquote|pre|code|img|hr|font|mark|small|big|strike|del|ins|table|thead|tbody|tr|td|th)(\s[^>]*)?\/?>/gi, '');
  return decodeEntities(lines)
    .replace(/\u200B/g, '')
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/^\n+|\n+$/g, '');
}

function hasText(text: string): boolean {
  return promptText(text).length > 0;
}

export function domainsOf(a: LibraryAssignment): Domain[] {
  const speaking = hasText(a.speakingPrompt) || !!a.speakingFile;
  const writing = a.separateWritingPrompt ? hasText(a.writingPrompt) || !!a.writingFile : speaking;
  return [...(speaking ? ['Speaking' as const] : []), ...(writing ? ['Writing' as const] : [])];
}

export function promptOf(a: LibraryAssignment): string {
  const speaking = promptText(a.speakingPrompt);
  return speaking !== '' ? speaking : a.separateWritingPrompt ? promptText(a.writingPrompt) : '';
}

export type Prompt = { text: string; file?: LibraryFile };

export type PromptBlock = Prompt & { domains: Domain[]; label: string };

export function isImage(file: LibraryFile): boolean {
  return file.fileType.toLowerCase().startsWith('image/');
}

export function pictureOf(a: LibraryAssignment): LibraryFile | undefined {
  return a.image && isImage(a.image) ? a.image : undefined;
}

export function promptFor(a: LibraryAssignment, domain: Domain): Prompt {
  const text = domain === 'Writing' && a.separateWritingPrompt ? a.writingPrompt : a.speakingPrompt;
  return { text: promptText(text), file: pictureOf(a) };
}

export function promptBlocks(a: LibraryAssignment): PromptBlock[] {
  const domains = domainsOf(a);
  if (!a.separateWritingPrompt) {
    return domains.length === 0 ? [] : [{ ...promptFor(a, 'Speaking'), domains, label: 'Speaking and writing prompt' }];
  }
  return domains.map((domain, i) => ({ ...promptFor(a, domain), file: i === 0 ? pictureOf(a) : undefined, domains: [domain], label: `${domain} prompt` }));
}

export function gradeLabel(grades: string[]): string {
  if (grades.length === 0) return '';
  return grades.length === 1 ? `Grade ${grades[0]}` : `Grades ${grades.join(', ')}`;
}

export function smallCaseload(a: LibraryAssignment, caseloads: LibraryCaseload[]): boolean {
  const schools = a.schoolIds.length > 0 ? a.schoolIds : [''];
  return schools.some((schoolId) => (caseloads.find((c) => c.teacherUserId === a.teacherUserId && c.schoolId === schoolId)?.students ?? 0) < LIBRARY.smallCaseload);
}

export function teacherName(a: LibraryAssignment): string {
  return `${a.teacherFirstName} ${a.teacherLastName}`.trim();
}

export function schoolNames(a: LibraryAssignment, schools: LibrarySchool[]): string {
  return a.schoolIds.map((id) => schools.find((s) => s.id === id)?.name ?? id).join(', ');
}

export function dayLabel(iso: string): string {
  return `${monthLabel(iso.slice(0, 7))} ${Number(iso.slice(8, 10))}`;
}

export function longDayLabel(iso: string): string {
  return `${dayLabel(iso)}, ${iso.slice(0, 4)}`;
}

export function tagsOf(a: LibraryAssignment, caseloads: LibraryCaseload[]): string[] {
  return [typeLabel(a), ...domainsOf(a), ...(smallCaseload(a, caseloads) ? ['Small caseload'] : [])];
}

function matches(a: LibraryAssignment, filters: Filters, skipType: boolean): boolean {
  const schoolOn = a.schoolIds.length === 0 || a.schoolIds.some((id) => !filters.schoolsOff.has(id));
  const domainOn = domainsOf(a).some((d) => !filters.domainsOff.has(d));
  const teacherOn = filters.teachers.size === 0 || filters.teachers.has(a.teacherUserId);
  const typeOn = skipType || filters.type === 'all' || typeLabel(a) === filters.type;
  return schoolOn && domainOn && teacherOn && typeOn;
}

export function applyFilters(list: LibraryAssignment[], filters: Filters): LibraryAssignment[] {
  return list.filter((a) => matches(a, filters, false)).sort((a, b) => b.startAt.localeCompare(a.startAt));
}

export function typeChips(list: LibraryAssignment[], filters: Filters): Chip[] {
  const base = list.filter((a) => matches(a, filters, true));
  const others = Array.from(new Set(list.map(typeLabel))).filter((t) => !FIXED_TYPES.includes(t)).sort((x, y) => x.localeCompare(y));
  return [
    { key: 'all', label: 'All', count: base.length },
    ...[...FIXED_TYPES, ...others].map((type) => ({ key: type, label: type, count: base.filter((a) => typeLabel(a) === type).length })),
  ];
}

export function schoolOptions(report: LibraryReport): { id: string; name: string; count: number }[] {
  return report.schools.map((s) => ({ id: s.id, name: s.name, count: report.assignments.filter((a) => a.schoolIds.includes(s.id)).length }));
}

export function domainOptions(list: LibraryAssignment[]): { domain: Domain; count: number }[] {
  return DOMAINS.map((domain) => ({ domain, count: list.filter((a) => domainsOf(a).includes(domain)).length }));
}

export function teacherOptions(list: LibraryAssignment[]): { userId: string; name: string; count: number }[] {
  const seen = new Map<string, LibraryAssignment>();
  for (const a of list) if (!seen.has(a.teacherUserId)) seen.set(a.teacherUserId, a);
  return Array.from(seen.values())
    .sort((x, y) => x.teacherLastName.localeCompare(y.teacherLastName) || x.teacherFirstName.localeCompare(y.teacherFirstName) || x.teacherUserId.localeCompare(y.teacherUserId))
    .map((a) => ({ userId: a.teacherUserId, name: teacherName(a), count: list.filter((b) => b.teacherUserId === a.teacherUserId).length }));
}

export const CSV_COLUMNS = ['Title', 'Type', 'Teacher', 'School', 'Grade', 'Assigned on', 'Speaking prompt', 'Writing prompt', 'Assigned', 'Submitted', 'Scored', 'Not started'];

export function csvRow(a: LibraryAssignment, report: LibraryReport): (string | number)[] {
  const domains = domainsOf(a);
  return [
    a.title, typeLabel(a), teacherName(a), schoolNames(a, report.schools), gradeLabel(a.grades), a.startAt,
    domains.includes('Speaking') ? promptFor(a, 'Speaking').text : '', domains.includes('Writing') ? promptFor(a, 'Writing').text : '',
    a.assigned, a.submitted, a.scored, a.assigned - a.submitted,
  ];
}

export function entryCsv(a: LibraryAssignment, report: LibraryReport): string {
  return csvText(CSV_COLUMNS, [csvRow(a, report)]);
}

const PROMPT_CHARS_PER_LINE = 58;
const PROMPT_CHARS_PER_LINE_BESIDE_PICTURE = 40;
const LINE_HEIGHT = 28;
const BLOCK_HEADER = 32;
const ENTRY_HEADER = 64;
const PICTURE_HEIGHT = 107;

function charsPerLine(block: Prompt): number {
  return block.file ? PROMPT_CHARS_PER_LINE_BESIDE_PICTURE : PROMPT_CHARS_PER_LINE;
}

function lineCount(text: string, perLine: number): number {
  return text.split('\n').reduce((n, line) => n + Math.max(1, Math.ceil(line.length / perLine)), 0);
}

function blockHeight(block: PromptBlock): number {
  return BLOCK_HEADER + Math.max(lineCount(block.text, charsPerLine(block)) * LINE_HEIGHT, block.file ? PICTURE_HEIGHT : 0);
}

function blocksHeight(blocks: PromptBlock[]): number {
  return ENTRY_HEADER + blocks.reduce((sum, block) => sum + blockHeight(block), 0);
}

export function estimateEntryHeight(a: LibraryAssignment): number {
  return blocksHeight(promptBlocks(a));
}

export type PrintPiece = { assignment: LibraryAssignment; number: number; blocks: PromptBlock[]; continued: boolean };

export function pieceHeight(piece: PrintPiece): number {
  return blocksHeight(piece.blocks);
}

function splitParagraph(line: string, perLine: number, maxLines: number): string[] {
  if (Math.ceil(line.length / perLine) <= maxLines) return [line];
  const limit = perLine * maxLines;
  const chunks: string[] = [];
  let rest = line;
  while (rest.length > limit) {
    const cut = rest.lastIndexOf(' ', limit);
    const at = cut > 0 ? cut : limit;
    chunks.push(rest.slice(0, at));
    rest = rest.slice(at).trimStart();
  }
  chunks.push(rest);
  return chunks;
}

function splitBlock(block: PromptBlock, maxLines: number): string[] {
  const perLine = charsPerLine(block);
  const chunks: string[] = [];
  let current: string[] = [];
  let used = 0;
  const flush = () => {
    if (current.length > 0) chunks.push(current.join('\n'));
    current = [];
    used = 0;
  };
  for (const line of block.text.split('\n')) {
    for (const part of splitParagraph(line, perLine, maxLines)) {
      const lines = Math.max(1, Math.ceil(part.length / perLine));
      if (used + lines > maxLines && current.length > 0) flush();
      current.push(part);
      used += lines;
    }
  }
  flush();
  return chunks;
}

function piecesOf(assignment: LibraryAssignment, number: number, pageHeight: number): PrintPiece[] {
  const blocks = promptBlocks(assignment);
  if (blocksHeight(blocks) <= pageHeight) return [{ assignment, number, blocks, continued: false }];
  const maxLines = Math.max(1, Math.floor((pageHeight - ENTRY_HEADER - BLOCK_HEADER) / LINE_HEIGHT));
  const pieces: PrintPiece[] = [];
  let current: PromptBlock[] = [];
  const flush = () => {
    if (current.length > 0) pieces.push({ assignment, number, blocks: current, continued: pieces.length > 0 });
    current = [];
  };
  for (const block of blocks) {
    for (const [i, text] of splitBlock(block, maxLines).entries()) {
      const next: PromptBlock = { ...block, text, file: i === 0 ? block.file : undefined };
      if (current.length > 0 && blocksHeight([...current, next]) > pageHeight) flush();
      current.push(next);
    }
  }
  flush();
  return pieces;
}

export function printPages(entries: LibraryAssignment[], pageHeight: number, firstPageHeight: number = pageHeight): PrintPiece[][] {
  const pages: PrintPiece[][] = [[]];
  let used = 0;
  const budgetOf = (pageIndex: number) => (pageIndex === 0 ? firstPageHeight : pageHeight);
  const place = (piece: PrintPiece) => {
    const height = pieceHeight(piece);
    const page = pages[pages.length - 1]!;
    if (page.length > 0 && used + height > budgetOf(pages.length - 1)) {
      pages.push([piece]);
      used = height;
    } else {
      page.push(piece);
      used += height;
    }
  };
  entries.forEach((entry, index) => {
    const whole = estimateEntryHeight(entry);
    const current = pages.length - 1;
    const startsOnCurrent = pages[current]!.length === 0 || used + whole <= budgetOf(current);
    const startBudget = startsOnCurrent ? budgetOf(current) : pageHeight;
    for (const piece of piecesOf(entry, index + 1, startBudget)) place(piece);
  });
  return pages;
}

export function picksKey(granularity: Granularity, start: string | undefined): string {
  return `assignment-library.picks.${granularity}.${start ?? 'current'}`;
}

export function readPicks(key: string): string[] {
  try {
    const raw = window.sessionStorage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

export function writePicks(key: string, ids: string[]): void {
  try {
    if (ids.length === 0) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, JSON.stringify(ids));
  } catch {
    return;
  }
}
